import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '../Components/AuthContext';
import { ThemeProvider } from './theme/ThemeProvider';
import { DeveloperModeProvider } from './layout/DeveloperModeContext';
import AppV2Routes from './routes';
import { installErrorReporter } from './lib/errorReporter';
import { preventIosInputZoom } from './lib/iosViewport';
import './styles/tokens.css';
/**
 * Tema de la sección Developer. Va global —no como módulo— porque define **tokens**, y una clase
 * con nombre estable es lo que permite aplicarlo desde cualquier pantalla de esa sección con un
 * `className` y sin importar nada.
 */
import './styles/devTheme.css';
import base from './styles/base.module.css';

/**
 * 🔑 **Se instala al cargar el módulo, no dentro de un `useEffect`.**
 *
 * Un error que ocurra durante el primer render —el caso más grave, el que deja la pantalla en
 * blanco— pasaría **antes** de que corriera cualquier efecto. Enganchando aquí, los escuchas
 * ya están puestos cuando React empieza a renderizar.
 *
 * Es idempotente por dentro, así que el doble montaje de StrictMode en desarrollo no duplica
 * temporizadores ni escuchas.
 */
installErrorReporter();

/**
 * 🔄 21-oct-2026: en iPhone, sin el zoom que Safari aplica al enfocar un campo (dejaba el login corrido a la derecha).
 * Al cargar el módulo, antes de que alguien toque un campo. Ver `lib/iosViewport.ts`.
 */
preventIosInputZoom();

/**
 * Punto de entrada del rediseño (V2).
 * - Providers propios; NO usa el ConfigProvider de Ant Design.
 * - Reutiliza AuthProvider (auth por cookies) que es agnóstico de UI.
 * - Se carga vía import() dinámico desde index.tsx solo cuando
 *   REACT_APP_UI_VERSION === 'v2', por lo que su CSS no entra al bundle de V1.
 */
const AppV2: React.FC = () => {
  return (
    <ThemeProvider>
      <div className={base.root}>
        <AuthProvider>
          {/* Dentro de `AuthProvider` porque necesita los permisos del usuario para
              saber si el interruptor de Developer siquiera aplica. */}
          <DeveloperModeProvider>
            <BrowserRouter>
              <AppV2Routes />
            </BrowserRouter>
          </DeveloperModeProvider>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
};

export default AppV2;
